import {useNavigate} from 'react-router';
import {useAuth} from '@common/auth/use-auth';
import {IconButton} from '@ui/buttons/icon-button';
import {FavoriteIcon} from '@ui/icons/material/Favorite';
import {FavoriteBorderIcon} from '@ui/icons/material/FavoriteBorder';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import {
  FavoriteTarget,
  favKey,
  useFavoriteIds,
  useToggleFavorite,
} from './favorites-queries';

/**
 * Heart toggle for a single opportunity listing. Filled when favorited,
 * outlined when not. Guests are sent to login (favorites need an account).
 *
 * IMPORTANT: it stops click/pointer propagation so it works inside a card that
 * is itself a link — clicking the heart toggles the favorite without opening
 * the listing.
 */
export function FavoriteButton({
  target,
  size = 'sm',
  className = '',
  /** Solid pill background — for placing over a photo/hero. */
  onSurface = false,
}: {
  target: FavoriteTarget;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  onSurface?: boolean;
}) {
  const {isLoggedIn} = useAuth();
  const navigate = useNavigate();
  const {data: ids} = useFavoriteIds();
  const toggle = useToggleFavorite();

  const favorited = !!ids?.has(favKey(target.listing_type, target.listing_id));

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      toast(message('Sign in to save favorites'));
      navigate('/login');
      return;
    }
    toggle.mutate(target);
  };

  return (
    <IconButton
      size={size}
      radius="rounded-full"
      onClick={onClick}
      onPointerDown={e => e.stopPropagation()}
      aria-pressed={favorited}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      className={`${
        onSurface ? 'bg-white/90 shadow-sm ring-1 ring-black/5 backdrop-blur hover:bg-white' : ''
      } ${favorited ? 'text-danger' : onSurface ? 'text-slate-600' : 'text-muted hover:text-danger'} ${className}`}
    >
      {favorited ? <FavoriteIcon /> : <FavoriteBorderIcon />}
    </IconButton>
  );
}
